<template>
  <div class="uk-flex uk-flex-middle uk-animation-fade" uk-height-viewport="offset-bottom: 40px">
    <div class="uk-width-1-1">
      <div class="uk-container">
        <div class="uk-grid-margin uk-grid uk-grid-stack" uk-grid>
          <div class="uk-width-1-1@m">
            <div class="uk-flex uk-flex-center">
              <div v-if="error" class="uk-alert-danger uk-width-large" uk-alert>
                <a class="uk-alert-close" uk-close />
                <p v-html="error" />
              </div>
            </div>

            <div class="uk-margin uk-width-large uk-margin-auto uk-card uk-card-default uk-box-shadow-large">
              <div class="uk-card-header uk-text-center">
                <img src="@/assets/img/logo.png" :alt="`${$globals.app.name.short} - ${$globals.app.name.long} Logo`" style="height: 70px;">
              </div>
              <div class="uk-card-body">
                <div v-if="type === 'agent'" class="uk-text-center">
                  We use Google for authentication agents. Please click the button below to start the authentication process or to use default login method.
                </div>

                <template v-else>
                  <form class="form" @submit.prevent="login">
                    <div class="uk-margin">
                      <div class="uk-inline uk-width-1-1">
                        <span class="uk-form-icon"><i class="gg-mail" /></span>
                        <input v-model="email" class="uk-input uk-form-large" type="text" placeholder="Email" autocomplete="email" data-cy="login-email">
                      </div>
                    </div>
                    <div class="uk-margin">
                      <div class="uk-inline uk-width-1-1">
                        <span class="uk-form-icon"><i class="gg-lock" /></span>
                        <input v-model="password" class="uk-input uk-form-large" type="password" placeholder="Password" autocomplete="current-password" data-cy="login-password" @keyup.enter="login">
                      </div>
                    </div>
                  </form>
                </template>
              </div>
              <div class="uk-card-footer uk-text-center">
                <template v-if="type === 'agent'">
                  <button class="uk-button uk-button-primary" style="margin-right: 1em;" @click="login">Google Login</button>
                  <router-link :to="{ name: 'login', query: null }" class="uk-button uk-button-secondary">Default Login</router-link>
                </template>

                <template v-else>
                  <button class="uk-button uk-button-primary uk-button-large uk-width-1-2" :disabled="!email || !password" data-cy="login-submit" @click="login">Login</button>
                </template>
              </div>
            </div>

            <div class="uk-text-center">
              <button class="uk-button uk-button-secondary uk-button-small uk-margin-small-right" data-cy="login-forgot-button" @click="forgotPassword">Forgot password?</button>
              <button class="uk-button uk-button-danger uk-button-small" uk-toggle="target: #modal-support" data-cy="login-support-button">Support</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div id="modal-support" class="uk-modal-full" uk-modal>
      <div class="uk-modal-dialog">
        <button class="uk-modal-close-full uk-close-large" type="button" uk-close data-cy="login-support-close" />
        <div class="uk-flex uk-flex-middle uk-flex-center" uk-height-viewport>
          <div class="uk-width-xlarge uk-padding-large">
            <div class="uk-text-center">
              <img src="@/assets/img/logo.png" :alt="`${$globals.app.name.short} - ${$globals.app.name.long} Logo`" style="height: 80px;">
              <h3>Support</h3>
            </div>
            <p class="uk-text-justify">Please contact administrator if you experience problems with Log In process.</p>
          </div>
        </div>
      </div>
    </div>

    <div id="modal-forgot-password" uk-modal data-cy="login-forgot-modal">
      <div class="uk-modal-dialog">
        <form class="uk-form-stacked">
          <div class="uk-modal-body">
            <div class="uk-text-center"><b>Note:</b> The email must be already attached to an existing account!</div>
            <label><div class="uk-margin">Your Email:</div></label>
            <input class="uk-input" data-cy="login-forgot-email">
          </div>
          <div class="uk-modal-footer uk-text-right">
            <button class="uk-button uk-button-default uk-modal-close" type="button">Cancel</button>
            <button class="uk-button uk-button-primary">Ok</button>
          </div>
        </form>
      </div>
    </div>

    <div class="footer uk-background-secondary">
      Powered by <b>Synago</b>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ViewLogin',

  data() {
    return {
      email: null,
      password: null
    }
  },

  computed: {
    error() {
      let response = false
      const error = this.$store.state.error.login

      if (error) {
        if (error.code === 'auth/wrong-password') {
          response = 'Username and password combination do not match our records. (x400-1)'
        }

        if (error.code === 'auth/user-not-found') {
          response = 'Username and password combination do not match our records. (x400-2)'
        }

        if (!response) {
          response = `Something went wrong! Please contact support. (x400-0) <br><br> Error: ${btoa(error.code)}`
        }
      }

      return response
    },

    type() {
      return (this.$route.query.type && this.$route.query.type === 'agent') ? 'agent' : 'default'
    }
  },

  methods: {
    async login() {
      // Clean up any leftover login error messages
      this.$store.commit('setError', { type: 'login', value: null })
      this.$store.commit('setLoading', true)
      await this.$firebase.auth().setPersistence(this.$firebase.auth.Auth.Persistence.SESSION)

      if (this.type === 'agent') {
        this.$store.commit('account/setType', 'agent')
        this.loginSSO()
      } else {
        this.$store.commit('account/setType', 'default')
        this.loginEP()
      }
    },

    loginEP() {
      this.$firebase.auth().signInWithEmailAndPassword(this.email, this.password)
        .catch((error) => {
          this.$store.commit('setLoading', false)
          this.$store.commit('setError', { type: 'login', value: error })
        })
    },

    loginSSO() {
      const provider = new this.$firebase.auth.GoogleAuthProvider()
      this.$firebase.auth().signInWithPopup(provider)
        .catch((error) => {
          this.$store.commit('setLoading', false)
          this.$store.commit('setError', { type: 'login', value: error })
        })
    },

    forgotPassword() {
      this.$uikit.modal.prompt('<div class="uk-text-center"><b>Note:</b> The email must be already attached to an existing account!</div><div class="uk-margin">Your Email:</div>', '').then((email) => {
        if (email) {
          const loader = this.$loader.show()

          this.$firebase.auth().sendPasswordResetEmail(email).then(() => {
            this.$uikit.notification('Password Reset Email with instructions was sent successfully!', { status: 'success', pos: 'top-center' })
          })
            .catch(() => {
              this.$uikit.notification('There was an issue sending the Password Reset Email with instructions!<br><br>If you are sure that the entered email was correct, please contact support!', { status: 'danger', pos: 'top-center' })
            })
            .finally(() => {
              loader.hide()
            })
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  .login {
    .footer {
      position: absolute;
      bottom: 0;
      left: 0;
      box-sizing: border-box;
      width: 100%;
      height: 40px;
      color: $colour-light;
      font-size: .8em;
      line-height: 40px;
      text-align: center;
    }
  }
</style>
